import React, { useCallback, useMemo, useState } from 'react';
import {
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import { Info } from 'lucide-react';
import type { ClinicPhysicianResponse } from '../../../../services/api/types.gen';
import { PermissionSlug } from '../../../../constants/featureFlags';
import { useStore } from '../../../../store';
import { formatPhysicianName } from '../../../../utils/formatting';
import { formatOrderDeliveryPreference } from '../../../../utils/orderDeliveryPreference';
import { useClinicContext } from '../../ClinicContext';
import { useClinicPhysicians } from '../../useClinicPhysicians';
import { useSuppliedPhysicianRoster } from '../../physicianRoster';
import { AddPhysicianDialog } from '../../AddPhysicianDialog';
import { PhysicianDetails } from '../../PhysicianDetails';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { QidTag } from '../../../dev/QidTag';
import {
  joinClasses,
  BESPOKE_FIELD_LABEL_CLASS,
  BESPOKE_FIELD_STACK_GAP_CLASS,
  BESPOKE_INPUT_COMPACT_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';

// The same slug the create-physician endpoint enforces. Backfilled from the
// coarse admin slugs, so everyone who could add a physician from here still can.
const CREATE_PERMISSIONS = [PermissionSlug.CLINIC_PHYSICIANS_EDIT];

export interface OASISPhysicianProps {
  qid: string;
  /** Display-only replacement for the schema prompt; answer binding still uses qid. */
  labelOverride?: string;
  className?: string;
  style?: React.CSSProperties;
  hideLabel?: boolean;
  innerClassName?: string;
  labelClassName?: string;
  /** Label + actions row. */
  headerClassName?: string;
  /** Info / add-new button cluster. */
  actionsClassName?: string;
  /**
   * Dropdown control, and the free-text input it falls back to. Compact
   * sizing is applied per control rather than shared: the fallback is a bare
   * input whose height this sets directly, while the dropdown's height comes
   * from react-select's own control, so the same `h-*` would mean two
   * different things.
   */
  controlClassName?: string;
  /** Dropdown menu row. */
  optionRowClassName?: string;
  /** Secondary metadata line under each option. */
  optionLabelClassName?: string;
  /** Details popover content. */
  detailsClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  inputClassName?: string;
}

function physicianOptionLabel(physician: ClinicPhysicianResponse): string {
  return `${formatPhysicianName(physician)} (${physician.npi})`;
}

/**
 * The metadata worth carrying on a dropdown row: enough to tell two similarly
 * named physicians apart at a glance, without wrapping the row. The rest is a
 * click away in the details popover.
 */
function physicianOptionSummary(physician: ClinicPhysicianResponse): string {
  return [
    physician.npi ? `NPI ${physician.npi}` : null,
    formatOrderDeliveryPreference(physician.order_delivery_preference),
    physician.phone,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Attending physician field: choose a physician from the clinic's roster and
 * store their NPI.
 *
 * Typing an NPI by hand is error prone: a clinician can transpose digits, or
 * enter a valid NPI belonging to the wrong physician, and neither mistake is
 * visible on review. Choosing a name instead makes the stored NPI correct by
 * construction while keeping the answer a bare NPI string, which is what
 * prefill writes and what the OASIS export reads. Layouts that still bind the
 * qid to `OASISText` keep the text input; the picker is what authoring this
 * node type opts into.
 *
 * Dropdown rows and a details popover carry the physician's own metadata —
 * NPI, order delivery preference, contact details — because a name alone
 * still leaves two similarly named physicians indistinguishable, which is the
 * same wrong-physician mistake in a different costume.
 *
 * Falls back to a free-text input whenever the roster cannot back the answer
 * (no clinic in context, load failure, empty roster) so the field is never
 * less usable than the plain input it replaces.
 *
 * No render-prop `children` slot, unlike the other field primitives: the point
 * of the node is the picker, and a bespoke control in its place would be an
 * NPI typed by hand under a different name.
 */
export function OASISPhysician({
  qid,
  labelOverride,
  className,
  style,
  hideLabel,
  innerClassName,
  labelClassName,
  headerClassName,
  actionsClassName,
  controlClassName,
  optionRowClassName,
  optionLabelClassName,
  detailsClassName,
  inputClassName,
}: OASISPhysicianProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const question = schemaMap.get(qid);
  const disabled = useQuestionFieldDisabled(question);

  const value = (answers[qid] as string) ?? '';
  const onChange = useCallback(
    (npi: string) => onAnswerChange(qid, npi),
    [onAnswerChange, qid]
  );
  const label = hideLabel
    ? undefined
    : (labelOverride ?? question?.question ?? question?.description ?? qid);
  const authoredControlClassName = resolveControlClassName(
    controlClassName,
    inputClassName
  );
  // Authored classes reach the bare input directly. They deliberately do not
  // reach the dropdown: its box is drawn by react-select, and a class on the
  // wrapper around it does nothing -- the dropdown takes its size from the
  // `sm` variant instead, which is the same 28px / 12px the palette asks for.
  const fallbackInputClassName = joinClasses(
    BESPOKE_INPUT_COMPACT_CLASS,
    authoredControlClassName
  );

  const { agencyClinicId } = useClinicContext();
  const hasAnyPermission = useStore(state => state.hasAnyPermission);
  const [isAddOpen, setIsAddOpen] = useState(false);

  // A supplied roster means an ancestor is standing in for the clinic, so the
  // hook is held at its documented null no-op rather than pointed at a clinic
  // that does not exist. Everything below reads one roster either way.
  // The menu portals to a document, and which document that is depends on
  // where this field ended up: a visit renders in the page, a layout preview
  // renders in an iframe. Ask the rendered node rather than guessing.
  const [node, setNode] = useState<HTMLDivElement | null>(null);

  const supplied = useSuppliedPhysicianRoster();
  const fetched = useClinicPhysicians(supplied ? null : agencyClinicId);
  const { physicians, isLoading, error, refetch, canCreate, createClinicId } =
    supplied ?? {
      ...fetched,
      canCreate: hasAnyPermission(CREATE_PERMISSIONS),
      createClinicId: agencyClinicId,
    };

  const physicianByNpi = useMemo(
    () => new Map(physicians.map(p => [p.npi as string, p])),
    [physicians]
  );
  // Null for an answer the roster cannot explain, which has no metadata to
  // show, so the details affordance is not offered for it.
  const selectedPhysician = value ? (physicianByNpi.get(value) ?? null) : null;

  const options = useMemo(() => {
    // NPI uniqueness per clinic is enforced by a partial unique index, so
    // these option values cannot collide.
    const built = physicians.map(physician => ({
      value: physician.npi as string,
      label: physicianOptionLabel(physician),
    }));
    // Keep an answer the roster cannot explain (carried forward from another
    // clinic, or the physician was removed) visible and selected instead of
    // silently dropping it. Not while the roster is still in flight, when
    // every answer is unexplained only because nothing has arrived yet.
    if (!isLoading && value && !built.some(option => option.value === value)) {
      built.unshift({ value, label: `${value} (not in this clinic)` });
    }
    return built;
  }, [physicians, value, isLoading]);

  // With no roster to choose from, typing the NPI is still better than an
  // empty dropdown, so the plain input stands in. "Add new" stays offered
  // alongside it so an empty clinic can be populated from here.
  //
  // Counted on the roster rather than on the options: an existing answer pins
  // itself as an option, and letting that pin stand in for a roster would trap
  // an empty clinic on a dropdown whose only entry is the answer it cannot
  // explain — the one case where the field must stay editable.
  // "Is there a roster to choose from" -- which a supplied one answers on its
  // own, without a clinic behind it.
  const showFallbackInput =
    (!supplied && !agencyClinicId) ||
    Boolean(error) ||
    (!isLoading && physicians.length === 0);

  // Dropdown rows lead with the name and carry the identifying metadata on a
  // second line. The closed control stays on one line: the field sits in
  // dense OASIS layouts where a two-line control would shift the grid.
  const formatOptionLabel = useCallback(
    (
      option: { value: string; label: string },
      meta: { context: 'menu' | 'value' }
    ) => {
      const physician = physicianByNpi.get(option.value);
      if (!physician || meta.context === 'value') return option.label;
      return (
        <span
          className={joinClasses('flex min-w-0 flex-col', optionRowClassName)}
        >
          <span className='truncate'>{formatPhysicianName(physician)}</span>
          <span
            className={joinClasses(
              'truncate text-xs text-muted-foreground',
              optionLabelClassName
            )}
          >
            {physicianOptionSummary(physician)}
          </span>
        </span>
      );
    },
    [physicianByNpi, optionRowClassName, optionLabelClassName]
  );

  return (
    <div
      ref={setNode}
      data-qid={qid}
      className={joinClasses('min-w-0', className)}
      style={style}
    >
      <QidTag qid={qid} />
      <div className={innerClassName ?? BESPOKE_FIELD_STACK_GAP_CLASS}>
        {/* With `hideLabel` the row has a single child, so `justify-between`
         * would collapse to flex-start and drop the buttons under the prompt
         * instead of opposite it. */}
        <div
          className={joinClasses(
            'flex items-baseline gap-2',
            label ? 'justify-between' : 'justify-end',
            headerClassName
          )}
        >
          {label ? (
            <label
              className={joinClasses(BESPOKE_FIELD_LABEL_CLASS, labelClassName)}
            >
              {label}
            </label>
          ) : null}
          <div
            className={joinClasses(
              'flex shrink-0 items-center gap-2',
              actionsClassName
            )}
          >
            {selectedPhysician && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type='button'
                    aria-label='Physician details'
                    title='Physician details'
                    className='text-muted-foreground hover:text-foreground'
                  >
                    <Info className='h-4 w-4' />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align='end'
                  className={detailsClassName ?? 'w-80'}
                >
                  <PhysicianDetails physician={selectedPhysician} />
                </PopoverContent>
              </Popover>
            )}
            {canCreate && !disabled && (
              <button
                type='button'
                className='text-xs font-medium text-primary hover:underline'
                onClick={createClinicId ? () => setIsAddOpen(true) : undefined}
              >
                + Add new
              </button>
            )}
          </div>
        </div>

        {showFallbackInput ? (
          <Input
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
            className={fallbackInputClassName}
            inputMode='numeric'
            maxLength={10}
          />
        ) : (
          <VirtualizedFormSelect
            options={options}
            value={value}
            disabled={disabled || isLoading}
            isClearable
            placeholder={
              isLoading ? 'Loading physicians...' : 'Select attending physician'
            }
            selectSize='sm'
            menuPortalTarget={node ? node.ownerDocument.body : null}
            onChange={e => onChange(e.target.value)}
            // Options are already sorted by name, and the "not in this clinic"
            // answer is pinned first on purpose.
            sortOptions={false}
            formatOptionLabel={formatOptionLabel}
          />
        )}

        {/* A silent drop to free text looks like the picker was never built, and
         * leaves a transient outage only escapable through the create-gated add
         * dialog. Say what happened and offer the retry to everyone. */}
        {error && (
          <p className='mt-1 text-xs text-muted-foreground'>
            {error}{' '}
            <button
              type='button'
              className='font-medium text-primary hover:underline'
              onClick={refetch}
            >
              Retry
            </button>
          </p>
        )}

        {createClinicId && isAddOpen && (
          <AddPhysicianDialog
            open
            onOpenChange={setIsAddOpen}
            clinicId={createClinicId}
            onCreated={physician => {
              // Reload first so the new physician is a real option rather than
              // showing as an answer the roster cannot explain.
              refetch();
              if (physician.npi) onChange(physician.npi);
            }}
          />
        )}
      </div>
    </div>
  );
}

export const OASIS_PHYSICIAN_VIBECODE = `
**OASISPhysician** — attending physician picker. Binds a qid whose answer is the
physician's NPI (M0018) and renders a searchable dropdown of the clinic's
physicians instead of an NPI text box. Use it for the attending-physician NPI
question; use \`OASISText\` for every other free-text qid. Falls back to a plain
input when no clinic roster is available. No \`multiline\`, no \`children\`.

It has more parts than the other field primitives, so it has more style slots
than the shared four:

| Prop | Target |
|------|--------|
| \`className\` | Outer \`[data-qid]\` wrapper |
| \`innerClassName\` | Prompt + control stack |
| \`headerClassName\` | Prompt + actions row |
| \`labelClassName\` | Prompt |
| \`actionsClassName\` | Info / add-new button cluster |
| \`controlClassName\` | The free-text NPI input shown when no roster is available (the dropdown sizes itself) |
| \`optionRowClassName\` | Dropdown menu row |
| \`optionLabelClassName\` | Metadata line under each option |
| \`detailsClassName\` | Details popover (replaces the default \`w-80\`) |
`;
